import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { channels, deepChannels, softChannels } from '../utils/color';
import { readStore, writeStore } from '../utils/storage';
export interface ThemePreset {
  id: string;
  name: string;
  blurb: string;
  accent: string;
  secondary: string;
  base: string;
  panel: string;
  raised: string;
  line: string;
  cream: string;
  muted: string;
}
export const themePresets: ThemePreset[] = [{
  id: 'plum',
  name: 'Plum & Gold',
  blurb: 'the house sound',
  accent: '#F2C94C',
  secondary: '#6C5CE7',
  base: '#0D0B14',
  panel: '#17141F',
  raised: '#201B2C',
  line: '#2A2438',
  cream: '#EDEAE3',
  muted: '#9A93AE'
}, {
  id: 'midnight',
  name: 'Midnight Brass',
  blurb: 'cold blue, warm horn',
  accent: '#E9B949',
  secondary: '#4C7DE7',
  base: '#0A0E17',
  panel: '#121926',
  raised: '#1A2433',
  line: '#243040',
  cream: '#E8EDF5',
  muted: '#8B9AB3'
}, {
  id: 'ember',
  name: 'Ember Room',
  blurb: 'last call, low light',
  accent: '#FF9F6B',
  secondary: '#E7568C',
  base: '#150C0C',
  panel: '#1F1414',
  raised: '#2A1C1C',
  line: '#372424',
  cream: '#F5E9E2',
  muted: '#B0968F'
}, {
  id: 'velvet',
  name: 'Velvet Static',
  blurb: 'deep violet, silver edge',
  accent: '#CBB8FF',
  secondary: '#7B4BFF',
  base: '#0B0A16',
  panel: '#141122',
  raised: '#1D1830',
  line: '#28213D',
  cream: '#ECEAF5',
  muted: '#968FB8'
}];
interface ThemeValue {
  preset: ThemePreset;
  presetId: string;
  accent: string;
  secondary: string;
  setPreset: (id: string) => void;
  setAccent: (hex: string) => void;
  setSecondary: (hex: string) => void;
  resetAccents: () => void;
  isCustomised: boolean;
}
const ThemeContext = createContext<ThemeValue | null>(null);
export function ThemeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [presetId, setPresetId] = useState(() => readStore<string>('themePreset', 'plum'));
  const [customAccent, setCustomAccent] = useState<string | null>(() => readStore<string | null>('themeAccent', null));
  const [customSecondary, setCustomSecondary] = useState<string | null>(() => readStore<string | null>('themeSecondary', null));
  const preset = useMemo(() => themePresets.find(p => p.id === presetId) ?? themePresets[0], [presetId]);
  const accent = customAccent ?? preset.accent;
  const secondary = customSecondary ?? preset.secondary;
  useEffect(() => {
    const root = document.documentElement;
    const vars: Record<string, string> = {
      '--accent': channels(accent),
      '--accent-soft': softChannels(accent),
      '--accent-deep': deepChannels(accent),
      '--secondary': channels(secondary),
      '--secondary-soft': softChannels(secondary),
      '--secondary-deep': deepChannels(secondary),
      '--surface-base': channels(preset.base),
      '--surface-panel': channels(preset.panel),
      '--surface-raised': channels(preset.raised),
      '--surface-line': channels(preset.line),
      '--cream': channels(preset.cream),
      '--muted': channels(preset.muted)
    };
    Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
    document.body.style.backgroundColor = preset.base;
  }, [accent, preset, secondary]);
  useEffect(() => writeStore('themePreset', presetId), [presetId]);
  useEffect(() => writeStore('themeAccent', customAccent), [customAccent]);
  useEffect(() => writeStore('themeSecondary', customSecondary), [customSecondary]);
  const setPreset = useCallback((id: string) => {
    setPresetId(id);
    setCustomAccent(null);
    setCustomSecondary(null);
  }, []);
  const resetAccents = useCallback(() => {
    setCustomAccent(null);
    setCustomSecondary(null);
  }, []);
  return <ThemeContext.Provider value={{
    preset,
    presetId,
    accent,
    secondary,
    setPreset,
    setAccent: setCustomAccent,
    setSecondary: setCustomSecondary,
    resetAccents,
    isCustomised: customAccent !== null || customSecondary !== null
  }} data-id="element-264" data-mp="1eu205tytdyqv">
      {children}
    </ThemeContext.Provider>;
}
export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside a ThemeProvider');
  return ctx;
}